import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import {
    BenchmarkRequestError, ComparisonDimension, DimensionDetailResponse, RunOutcome, benchmarkService
} from '../../../services/benchmarkService';
import { PanelError, PanelSkeleton, SectionCard } from '../BenchmarkPrimitives';
import { QualifiedBadge } from '../BenchmarkQualifiers';
import { count } from '../BenchmarkVocabulary';
import { WithheldIndex } from '../withheldMetrics';
import { conceptHref } from '../../Ontology/ontologyFormat';
import ComparisonSubjectsTable from './ComparisonSubjectsTable';
import DimensionMatrix, { CellSelection } from './DimensionMatrix';
import DimensionMetricsStrip from './DimensionMetricsStrip';
import {
    DIMENSION_TYPE_LABELS, comparisonStatusMeta, dimensionEvidence, dimensionQualifications, dimensionSentence,
    isClassDimension, producerLabel
} from './investigationVocabulary';
import { dsVar, ink } from '../../../theme/tokens';

interface DimensionDetailProps {
    runKey: number;
    /** The dimension's row from the run's manifest, with its counts and metrics. */
    summary: ComparisonDimension;
    withheld: WithheldIndex;
    onCaseSelect: (caseRef: string) => void;
    selectedCaseRef?: string | null;
    accent?: string;
    /** The outcomes the run declares, the one the figures lead with first. */
    outcomes?: RunOutcome[];
}

const subheadSx = {
    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' as const,
    color: dsVar('fg-muted'), mb: 1.25
};

/**
 * The cases behind a dimension's figures and the scorer's confidence in them.
 * Amber on the scorer's own judgement (low confidence, or below its minimum
 * support), not the run-wide case threshold the tier views use.
 */
export const SupportChip: React.FC<{ summary: ComparisonDimension }> = ({ summary }) => {
    const evidence = dimensionEvidence(summary);
    const agreement = summary.metrics.agreementRate?.all ?? summary.metrics.exactAgreement?.all;
    const weak = evidence.confidence === 'low' || Boolean(agreement?.indicative);
    const color = weak ? '#FBBF24' : '#94A3B8';
    return (
        <Tooltip arrow title={weak
            ? 'Too few comparable cases, or low confidence by the run\'s own measure: read these figures as indicative only.'
            : 'The comparable cases behind these figures, and the run\'s own confidence in them.'}
        >
            <Box component="span" sx={{
                display: 'inline-flex', px: 1, py: 0.25, borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 600,
                whiteSpace: 'nowrap', color: ink(color), backgroundColor: `${color}1A`, border: `1px solid ${color}4D`, cursor: 'help'
            }}>
                {count(evidence.sampleSize)} comparable{evidence.confidence ? ` · ${evidence.confidence} confidence` : ''}
            </Box>
        </Tooltip>
    );
};

/** Why the cases outside the grid were not compared, so a gap is never read as agreement. */
const NotComparableList: React.FC<{ rows: DimensionDetailResponse['notComparable'] }> = ({ rows }) => {
    if (!rows.length) return null;
    return (
        <Box sx={{ mt: 2 }}>
            <Typography sx={subheadSx}>Not compared</Typography>
            {rows.map((r) => {
                const sides = [
                    r.platformMissing ? `the agent replay produced nothing for ${count(r.platformMissing)}` : null,
                    r.institutionMissing ? `the historical record has nothing for ${count(r.institutionMissing)}` : null
                ].filter(Boolean).join('; ');
                return (
                    <Typography key={r.status} sx={{ fontSize: '0.8125rem', color: dsVar('fg-2'), lineHeight: 1.7 }}>
                        <Box component="span" sx={{ color: dsVar('fg-soft'), fontWeight: 600 }}>{count(r.count)} cases</Box>
                        {' · '}{comparisonStatusMeta(r.status).label}{sides ? ` — ${sides}` : ''}
                    </Typography>
                );
            })}
        </Box>
    );
};

/** What heads a dimension wherever it opens: its support, the run's caveats, and its Risk Library entry. */
export const DimensionBadges: React.FC<{ summary: ComparisonDimension; withheld: WithheldIndex }> = ({ summary, withheld }) => {
    // A typology is the Risk Library's own entry (its key is the concept code), so the
    // reader can open its definition, red flags and stage. Red flags are not linked yet:
    // some catalogue codes have no Risk Library entry to open.
    const libraryHref = summary.dimensionType === 'typology' ? conceptHref(summary.dimensionKey) : null;
    return (
        <>
            <SupportChip summary={summary} />
            <QualifiedBadge qualifications={dimensionQualifications(withheld, summary)} />
            {libraryHref && (
                <Box
                    component="a"
                    href={libraryHref}
                    sx={{ fontSize: '0.75rem', fontWeight: 600, color: dsVar('accent-fg'), textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    Open in Risk Library
                </Box>
            )}
        </>
    );
};

/** What kind of item a dimension is and who produces it, for a heading's subtitle. */
const dimensionSubtitle = (summary: ComparisonDimension): string =>
    `${DIMENSION_TYPE_LABELS[summary.dimensionType] ?? summary.dimensionType} · produced by ${summary.producerLabel ?? producerLabel(summary.producer)}`
    + (summary.drivesDecision === false ? ' · compared only: it cannot move this review\'s decisions' : '');

type DimensionDetailBodyProps = Omit<DimensionDetailProps, 'withheld' | 'accent'>;

/**
 * A dimension's figures, its class-by-class grid with how many of each cell
 * proved genuine, why the remaining cases were not compared, and the cases
 * themselves — opening on the disagreements. Shared by the Decisions tab's
 * cards and the drawer a scorecard row opens.
 */
export const DimensionDetailBody: React.FC<DimensionDetailBodyProps> = ({
    runKey, summary, onCaseSelect, selectedCaseRef, outcomes
}) => {
    const [detail, setDetail] = useState<DimensionDetailResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [cell, setCell] = useState<CellSelection | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setCell(null);
        // Dropped, not kept while the next loads: the last item's grid and figures
        // would otherwise show under this item's heading until the fetch returns.
        setDetail(null);
        benchmarkService.getDimension(runKey, summary.dimensionKey)
            .then((data) => { if (!cancelled) setDetail(data); })
            .catch((err) => {
                if (!cancelled) setError(err instanceof BenchmarkRequestError ? err.message : 'Could not load this comparison.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [runKey, summary.dimensionKey, attempt]);

    const counts = summary.agreementCounts;
    // Opens on the disagreements: the cases a reviewer reads first.
    const initialAgreement = isClassDimension(summary)
        ? (counts.disagree ? 'disagree' : counts.adjacent ? 'adjacent' : undefined)
        : counts.platform_only ? 'platform_only' : counts.institution_only ? 'institution_only' : undefined;
    const runOutcomes = outcomes ?? detail?.outcomes;

    return (
        <>
            {summary.comparable > 0 && (
                <DimensionMetricsStrip dimension={summary} metrics={detail?.metrics ?? summary.metrics} outcomes={runOutcomes} />
            )}

            <Box sx={{ mt: 3 }}>
                {error ? (
                    <PanelError message={error} onRetry={() => setAttempt((n) => n + 1)} />
                ) : loading && !detail ? (
                    <PanelSkeleton height={180} />
                ) : detail ? (
                    <>
                        <Typography sx={subheadSx}>Historical against agent replay · select a cell to see its cases</Typography>
                        <DimensionMatrix
                            dimension={detail.dimension}
                            cells={detail.cells}
                            selected={cell}
                            onSelect={setCell}
                            outcomes={runOutcomes}
                        />
                        <NotComparableList rows={detail.notComparable} />
                    </>
                ) : null}
            </Box>

            {summary.subjects > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography sx={subheadSx}>Cases</Typography>
                    <ComparisonSubjectsTable
                        key={summary.dimensionKey}
                        runKey={runKey}
                        dimension={summary}
                        agreementCounts={counts}
                        cell={cell}
                        initialAgreement={initialAgreement}
                        onCaseSelect={onCaseSelect}
                        selectedCaseRef={selectedCaseRef}
                    />
                </Box>
            )}
        </>
    );
};

/**
 * One compared dimension in full, as a card: what it says in a sentence, the
 * caveats the run attached to it, then its figures, grid and cases.
 */
const DimensionDetail: React.FC<DimensionDetailProps> = ({
    runKey, summary, withheld, onCaseSelect, selectedCaseRef, accent = '#FFDE59', outcomes
}) => (
    <SectionCard
        title={summary.label}
        subtitle={dimensionSubtitle(summary)}
        icon={<CompareArrowsIcon fontSize="small" />}
        accent={accent}
        badges={<DimensionBadges summary={summary} withheld={withheld} />}
    >
        <Typography sx={{ fontSize: '0.9375rem', color: dsVar('fg-body'), lineHeight: 1.6, mb: 2.5 }}>
            {dimensionSentence(summary)}
        </Typography>
        <DimensionDetailBody
            runKey={runKey}
            summary={summary}
            onCaseSelect={onCaseSelect}
            selectedCaseRef={selectedCaseRef}
            outcomes={outcomes}
        />
    </SectionCard>
);

export default DimensionDetail;
